import { forwardRef } from 'react';

import { SupportedProcessors } from '../../constants';

import { AdyenProvider } from './AdyenProvider';
import { StripeProvider } from './StripeProvider';

export type PaymentProcessorType = Omit<
  keyof typeof SupportedProcessors,
  'PAYPAL' | 'DLOCAL'
>;

/**
 * Activate payment provider based on the given payment processor (Stripe,
 * Adyen). Returns a ref with the following functions accessible at
 * ref.current:
 *     - processPayment
 *     - createPaymentMethod
 *
 * Thin wrapper around the StripeProvider and AdyenProvider components.
 *
 * Usage:
 * ```
 * const paymentProviderRef = useRef();
 * const submitPayment = () => {
 *    paymentProviderRef.current.processPayment();
 * }
 * <PaymentProvider processor={processor} ref={paymentProviderRef} />
 * ```
 * TODO: refactor to use hooks or render props instead of useImperativeHandle
 *       & refs. useImperativeHandle is being used to pass functions up to the
 *       parent component, which is better handled with render props or hooks.
 */
export const PaymentProvider = forwardRef(
  ({ processor }: { processor: PaymentProcessorType }, ref) => {
    if (processor === SupportedProcessors.STRIPE) {
      return <StripeProvider ref={ref} />;
    }
    if (processor === SupportedProcessors.ADYEN) {
      return <AdyenProvider ref={ref} />;
    }

    console.warn(
      `[PaymentProvider] Processor ${processor} is not supported - returning null`,
    );
    return null;
  },
);
